import { useState, useCallback } from 'react';
import { compressImage } from '../utils/compressImage';

/**
 * Custom hook for managing image upload functionality
 * 
 * This hook handles:
 * - File validation and processing
 * - Image preview generation
 * - File size and type restrictions
 * - Multiple file management
 * - Integration with form fields
 * 
 * @returns {Object} Image upload state and functions
 */
export const useImageUpload = () => {
  // State for tracking upload status
  const [uploadStatus, setUploadStatus] = useState('idle'); // 'idle', 'processing', 'complete', 'error'
  const [uploadError, setUploadError] = useState(null);
  const [processingFiles, setProcessingFiles] = useState([]);

  // Configuration constants
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const MAX_FILES_PER_AREA = 10; // Maximum files per question area

  /**
   * Validates a single file against our requirements
   * 
   * @param {File} file - File to validate
   * @returns {Object} Validation result with isValid and error message
   */
  const validateFile = useCallback((file) => {
    // Check file type
    if (!ALLOWED_TYPES.includes(file.type.toLowerCase())) {
      return {
        isValid: false,
        error: `File type ${file.type} is not allowed. Please use JPEG, PNG, or WebP images.`
      };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        isValid: false,
        error: `File size (${(file.size / 1024 / 1024).toFixed(1)}MB) exceeds the maximum allowed size of ${MAX_FILE_SIZE / 1024 / 1024}MB.`
      };
    }

    // Check if filename is reasonable
    if (file.name.length > 100) {
      return {
        isValid: false,
        error: 'Filename is too long. Please use a shorter filename.'
      };
    }

    return { isValid: true, error: null };
  }, []);

  /**
   * Processes files for upload, including validation and naming
   * 
   * @param {FileList|Array} acceptedFiles - Files to process
   * @param {string} sectionIndex - Section index (1-based)
   * @param {string} areaIndex - Area index (1-based) 
   * @param {Array} existingFiles - Currently uploaded files for this area
   * @returns {Promise<Object>} Processing result with processed files and errors
   */
  const processFiles = useCallback(async (acceptedFiles, sectionIndex, areaIndex, existingFiles = []) => {
    setUploadStatus('processing');
    setUploadError(null);
    setProcessingFiles([]);

    const filesArray = Array.from(acceptedFiles);
    const processedFiles = [];
    const errors = [];

    // Check total file count
    if (existingFiles.length + filesArray.length > MAX_FILES_PER_AREA) {
      setUploadError(`Maximum ${MAX_FILES_PER_AREA} files allowed per area. You currently have ${existingFiles.length} files.`);
      setUploadStatus('error');
      return { processedFiles: [], errors: ['Too many files'] };
    }

    try {
      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        setProcessingFiles(prev => [...prev, file.name]);

        // Validate the file
        const validation = validateFile(file);
        if (!validation.isValid) {
          errors.push(`${file.name}: ${validation.error}`);
          continue;
        }

        // Compress the image before creating the standardized File
        const originalSize = file.size;
        const compressed = await compressImage(file);
        if (compressed !== file) {
          console.log(
            `[ImageUpload] Compressed ${file.name}: ${(originalSize / 1024).toFixed(0)}KB → ${(compressed.size / 1024).toFixed(0)}KB`
          );
        }

        // Create standardized filename (always .jpg after compression)
        const fileExtension = compressed.type === 'image/jpeg' ? 'jpg' : file.name.split('.').pop().toLowerCase();
        const timestamp = Date.now();
        const standardizedName = `${sectionIndex}-${areaIndex}-${timestamp}-${i}.${fileExtension}`;

        // Create new File object with standardized name
        const processedFile = new File([compressed], standardizedName, {
          type: compressed.type,
          lastModified: compressed.lastModified,
        });

        // Add metadata to the file object for easier handling
        processedFile.originalName = file.name;
        processedFile.uploadTimestamp = timestamp;
        processedFile.sectionIndex = sectionIndex;
        processedFile.areaIndex = areaIndex;

        processedFiles.push(processedFile);
      }

      if (errors.length > 0) {
        setUploadError(`Some files could not be processed: ${errors.join('; ')}`);
        setUploadStatus('error');
      } else {
        setUploadStatus('complete');
      }

    } catch (error) {
      setUploadError('An error occurred while processing files. Please try again.');
      setUploadStatus('error');
    } finally {
      setProcessingFiles([]);
    }

    return { processedFiles, errors };
  }, [validateFile]);

  /**
   * Generates preview URLs for image files
   * 
   * @param {Array} files - Array of File objects
   * @returns {Promise<Array>} Array of objects with file and preview URL
   */
  const generatePreviews = useCallback(async (files) => {
    const previews = [];

    for (const file of files) {
      try {
        let previewUrl = null;
        
        // Check if this is a saved image with a GCS URL
        if (file._isSavedImage && file._gcsUrl) {
          previewUrl = file._gcsUrl;
        } else if (file.type.startsWith('image/')) {
          // Generate preview for new image files
          previewUrl = URL.createObjectURL(file);
        }
        
        previews.push({
          file,
          previewUrl,
          name: file._originalName || file.originalName || file.name,
          isSavedImage: file._isSavedImage || false,
        });
      } catch (error) {
        previews.push({
          file,
          previewUrl: null,
          name: file._originalName || file.originalName || file.name,
          isSavedImage: file._isSavedImage || false,
          error: 'Could not generate preview',
        });
      }
    }

    return previews;
  }, []);

  /**
   * Removes a file from the processed files list
   * 
   * @param {Array} files - Current files array
   * @param {string} fileName - Name of file to remove
   * @returns {Array} Updated files array
   */
  const removeFile = useCallback((files, fileName) => {
    return files.filter(file => file.name !== fileName);
  }, []);

  /**
   * Cleans up preview URLs to prevent memory leaks
   * Only revokes blob URLs, not GCS URLs for saved images
   * 
   * @param {Array} previews - Array of preview objects with URLs
   */
  const cleanupPreviews = useCallback((previews) => {
    previews.forEach(preview => {
      // Only revoke blob URLs created with URL.createObjectURL()
      // Don't revoke GCS URLs for saved images
      if (preview.previewUrl && !preview.isSavedImage && preview.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(preview.previewUrl);
      }
    });
  }, []);

  /**
   * Gets formatted file information for display
   * 
   * @param {File} file - File object
   * @returns {Object} Formatted file information
   */
  const getFileInfo = useCallback((file) => {
    const displayName = file._originalName || file.originalName || file.name;
    const displaySize = file._isSavedImage && file._fileSize 
      ? (file._fileSize / 1024).toFixed(1) + ' KB'
      : (file.size / 1024).toFixed(1) + ' KB';
    
    return {
      name: displayName,
      size: displaySize,
      type: file.type,
      lastModified: new Date(file.lastModified).toLocaleDateString(),
      isSavedImage: file._isSavedImage || false,
      storageType: file._storageType || 'local',
    };
  }, []);

  /**
   * Checks if current upload state allows new uploads
   * 
   * @returns {boolean} True if uploads are allowed
   */
  const canUpload = useCallback(() => {
    return uploadStatus !== 'processing';
  }, [uploadStatus]);

  /**
   * Resets upload state to initial values
   */
  const resetUploadState = useCallback(() => {
    setUploadStatus('idle');
    setUploadError(null);
    setProcessingFiles([]);
  }, []);

  /**
   * Gets user-friendly status message
   * 
   * @returns {string} Status message for display
   */
  const getStatusMessage = useCallback(() => {
    switch (uploadStatus) {
      case 'processing':
        return processingFiles.length > 0 
          ? `Processing ${processingFiles.length} file(s)...`
          : 'Processing files...';
      case 'complete':
        return 'Files processed successfully';
      case 'error':
        return uploadError || 'An error occurred';
      default:
        return '';
    }
  }, [uploadStatus, processingFiles, uploadError]);

  return {
    // State
    uploadStatus,
    uploadError,
    processingFiles,
    
    // Configuration
    maxFileSize: MAX_FILE_SIZE,
    allowedTypes: ALLOWED_TYPES,
    maxFilesPerArea: MAX_FILES_PER_AREA,
    
    // Functions
    processFiles,
    generatePreviews,
    removeFile,
    cleanupPreviews,
    getFileInfo,
    canUpload,
    resetUploadState,
    getStatusMessage,
    
    // Computed properties
    isProcessing: uploadStatus === 'processing',
    hasError: uploadStatus === 'error',
    isComplete: uploadStatus === 'complete',
  };
};